<script lang="ts">
  // Activity · Route: the route as a glowing line over a map grid, the elevation profile,
  // and the key numbers.
  import type { ReportContext } from '../context'
  import { statsFor } from '../stats'
  import Badges from '../parts/Badges.svelte'
  import Brand from '../parts/Brand.svelte'
  import ElevationProfile from '../parts/ElevationProfile.svelte'
  import Fill from '../parts/Fill.svelte'
  import Frame from '../parts/Frame.svelte'
  import PowerChart from '../parts/PowerChart.svelte'
  import RouteMap from '../parts/RouteMap.svelte'
  import StatGrid from '../parts/StatGrid.svelte'
  import TitleBlock from '../parts/TitleBlock.svelte'

  export let ctx: ReportContext

  $: stats = statsFor(ctx, ['stat.distance', 'stat.time', 'stat.elevation', 'stat.xep', 'stat.power', 'stat.speed', 'stat.xss', 'stat.recovery', 'stat.difficulty', 'stat.hr']).slice(0, 6)
  $: route = ctx.show('route') ? ctx.data.route : null
  $: alt = ctx.show('elevation') ? ctx.data.series?.alt : null
  $: wide = ctx.size === 'landscape'
  $: elevHeight = { portrait: 100, square: 76, landscape: 110 }[ctx.size]
</script>

<Frame {ctx} base="linear-gradient(180deg, #121c21 0%, #0a1013 100%)">
  <svelte:fragment slot="decor">
    {#if route && !ctx.background}<div class="xr-grid" />{/if}
  </svelte:fragment>

  <div class="xr-layout">
    <div class="xr-top">
      <TitleBlock {ctx} size={ctx.size === 'square' ? 'md' : 'lg'} lines={2} />
      {#if !wide}<Brand height={38} />{/if}
    </div>

    <div class="xr-graphic">
      {#if route}
        <Fill minHeight={180} let:width let:height>
          <RouteMap {route} trim={ctx.options.trimRoute} {width} {height} strokeWidth={9} padding={30} />
        </Fill>
      {:else if ctx.show('chart') && ctx.data.series?.power}
        <Fill minHeight={160} let:width let:height>
          <PowerChart series={ctx.data.series} colored={ctx.options.colorPowerChart} showHr={ctx.options.chartHeartRate} {width} {height} showMpa={false} breakthrough={ctx.data.breakthrough?.level ?? null} />
        </Fill>
      {/if}
      {#if alt}
        <div class="xr-elev">
          <Fill minHeight={elevHeight} let:width>
            <ElevationProfile {alt} {width} height={elevHeight} color="var(--accent)" opacity={0.5} />
          </Fill>
        </div>
      {/if}
    </div>

    <div class="xr-stats"><StatGrid {stats} columns={wide ? 2 : 3} size="md" icons /></div>
    <div class="xr-badges"><Badges {ctx} own="activity" max={ctx.size === 'portrait' ? Infinity : 2} /></div>
    {#if wide}<div class="xr-brand"><Brand height={38} /></div>{/if}
  </div>
</Frame>

<style>
  .xr-grid {
    position: absolute;
    inset: 0;
    background-image:
      linear-gradient(rgba(255, 255, 255, 0.045) 2px, transparent 2px),
      linear-gradient(90deg, rgba(255, 255, 255, 0.045) 2px, transparent 2px);
    background-size: 90px 90px;
    background-position: -2px -2px;
    mask-image: radial-gradient(70% 55% at 50% 45%, black 30%, transparent 100%);
    -webkit-mask-image: radial-gradient(70% 55% at 50% 45%, black 30%, transparent 100%);
  }
  .xr-layout {
    flex: 1;
    min-height: 0;
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    grid-template-rows: auto minmax(0, 1fr) auto auto;
    grid-template-areas: 'top' 'graphic' 'stats' 'badges';
    row-gap: 30px;
  }
  :global(.xr-square) .xr-layout { row-gap: 22px; }
  /* Landscape: the ride's name and numbers on the left, the route filling the right. */
  :global(.xr-landscape) .xr-layout {
    grid-template-columns: 560px minmax(0, 1fr);
    grid-template-rows: auto minmax(0, 1fr) auto auto;
    grid-template-areas:
      'top graphic'
      'stats graphic'
      'badges graphic'
      'brand graphic';
    column-gap: 70px;
    row-gap: 30px;
  }
  .xr-top { grid-area: top; display: flex; align-items: flex-start; justify-content: space-between; gap: 40px; min-width: 0; }
  .xr-graphic { grid-area: graphic; display: flex; flex-direction: column; min-height: 0; }
  .xr-stats { grid-area: stats; }
  :global(.xr-landscape) .xr-stats { align-self: end; }
  .xr-badges { grid-area: badges; }
  .xr-badges:empty { display: none; }
  .xr-brand { grid-area: brand; }
  .xr-elev {
    display: flex;
    margin-top: 16px;
    border-bottom: 2px solid var(--faint);
  }
</style>
